/**
 * React/TypeScript code for WebView to Flutter Authentication Bridge
 *
 * This file contains the code you need to paste into your React webview app
 * to communicate with the Flutter app for Apple Sign-In authentication.
 *
 * Usage:
 * 1. Import the useFlutterAppleAuth hook
 * 2. Call appleLogin() when you need to trigger the Apple Sign-In flow
 * 3. Handle the returned Firebase ID token in your app
 */

import { useState } from 'react';
import { useIsFlutterWebView } from './useFlutterGoogleAuth';

// Type definitions
interface NativeLoginResponse {
  success: boolean;
  idToken: string | null;
  displayName: string | null;
  email: string | null;
  photoURL: string | null;
  uid: string | null;
  provider: 'google' | 'facebook' | 'apple';
  error: string | null;
}

/**
 * Request Apple Sign-In from Flutter app
 * Returns the full auth response if successful, null otherwise
 */
export const requestFlutterAppleLogin = async (): Promise<NativeLoginResponse | null> => {
  try {
    // Check if we're in a Flutter WebView
    if (!window.flutter_inappwebview) {
      console.warn('Not running in Flutter WebView. Apple login will not work.');
      return null;
    }

    console.log('🍎 Requesting Apple login from Flutter app...');

    // Call the Flutter handler
    const response = await window.flutter_inappwebview.callHandler('requestAppleLogin');

    if (response.success) {
      console.log('✅ Apple login successful');
      return response as NativeLoginResponse;
    } else {
      console.error('❌ Apple login failed:', response.error);
      return null;
    }
  } catch (error) {
    console.error('❌ Error calling Flutter Apple login handler:', error);
    return {
      success: false,
      idToken: null,
      displayName: null,
      email: null,
      photoURL: null,
      uid: null,
      provider: 'apple',
      error: String(error),
    };
  }
};

/**
 * Hook for Apple Sign-In via Flutter WebView
 */
export const useFlutterAppleAuth = () => {
  const [isLoading, setIsLoading] = useState(false);
  const isFlutterWebView = useIsFlutterWebView();

  const appleLogin = async (): Promise<NativeLoginResponse | null> => {
    if (!isFlutterWebView) {
      console.warn('Not running in Flutter WebView');
      return null;
    }

    setIsLoading(true);
    try {
      const response = await requestFlutterAppleLogin();
      return response;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    appleLogin,
    isLoading,
    isFlutterWebView,
  };
};

/**
 * Example usage with the hook:
 *
 * ```tsx
 * function MyComponent() {
 *   const { appleLogin, isLoading, isFlutterWebView } = useFlutterAppleAuth();
 *
 *   const handleAppleLogin = async () => {
 *     const response = await appleLogin();
 *     if (response?.success && response.idToken) {
 *       console.log('Apple login successful!', {
 *         email: response.email,
 *         displayName: response.displayName,
 *         uid: response.uid,
 *       });
 *       // Handle successful login
 *     } else {
 *       console.log('Apple login failed or cancelled:', response?.error);
 *     }
 *   };
 *
 *   // Only show button if running in Flutter WebView
 *   if (!isFlutterWebView) {
 *     return null;
 *   }
 *
 *   return (
 *     <button onClick={handleAppleLogin} disabled={isLoading}>
 *       {isLoading ? 'Signing in...' : 'Sign in with Apple'}
 *     </button>
 *   );
 * }
 * ```
 */
